import DownIcon from '@carbon/icons-react/es/arrow--down/32';
import UpIcon from '@carbon/icons-react/es/arrow--up/32';
import { Tooltip } from 'antd';
import { isNaN, round } from 'lodash';
import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { BaseWidget } from 'components/organisms/projects-report/common';
import { WidgetState } from 'components/organisms/projects-report/widgets';
import { TitleWithTooltip } from 'components/organisms/projects-report/widgets/components';
import {
  loadingRender,
  noDataMessageFormatter,
  noDataRender,
} from 'components/organisms/projects-report/widgets/renderers';
import { HelperText01, ProductiveHeading04 } from 'components/styles';
import { TranslationKey, useTranslate } from 'hooks/use-translation';

type CompareProps = {
  isGood: boolean;
  value: number;
  hasIncreased: number;
};

interface Props<T, E, CompareValueType extends CompareProps> {
  value: WidgetState<T, E>;
  title: string | ReactNode;
  tooltip?: string | ReactNode;
  noDataMsg: string | ReactNode;
  cmpValue?: CompareValueType;
  headerTransformer?: (result: T) => ReactNode;
  footerTransformer?: (result: T) => ReactNode;
  tooltipTransformer?: (result: T) => ReactNode;
}
const Result = styled.p`
  ${ProductiveHeading04};
  margin: 0;
`;

const FooterWrapper = styled.div`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  align-items: end;
`;

const FooterText = styled.p`
  ${HelperText01};
  color: var(--gray-100);
  margin: 0;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;

const CompareWrapper = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
`;

interface ArrowWithTooltipProps extends CompareProps {
  tooltip: string;
}
const ArrowWithToolTip: FC<ArrowWithTooltipProps> = ({
  hasIncreased,
  isGood,
  tooltip,
}) => {
  if (!hasIncreased) {
    return null;
  }
  const Arrow = hasIncreased > 0 ? UpIcon : DownIcon;

  return (
    <Tooltip title={tooltip}>
      <Arrow
        style={{
          fill: isGood ? 'var(--green-50)' : 'var(--red-60)',
        }}
      />
    </Tooltip>
  );
};

const DefaultTransformer = (res) => res;

export const TemplateInsightWidget: FC<Props<number, unknown, CompareProps>> =
  ({
    value,
    title,
    tooltip = null,
    noDataMsg,
    cmpValue,
    headerTransformer = DefaultTransformer,
    footerTransformer = DefaultTransformer,
    tooltipTransformer = DefaultTransformer,
  }) => {
    const translate = useTranslate();
    const widgetTooltip = (hasIncreased: boolean, suffix: string) =>
      translate(
        `reports.template_insight.widget.tooltip.${
          hasIncreased ? 'increased' : 'decreased'
        }` as TranslationKey,
        {
          suffix,
        },
      );

    const resultRenderer = (result: number) => (
      <Wrapper>
        <CompareWrapper>
          <Result>{headerTransformer(round(result))}</Result>
          {cmpValue && (
            <ArrowWithToolTip
              {...cmpValue}
              tooltip={widgetTooltip(
                cmpValue.hasIncreased > 0,
                tooltipTransformer(Math.abs(round(cmpValue.value - result))),
              )}
            />
          )}
        </CompareWrapper>
        {cmpValue && (
          <FooterWrapper>
            {!isNaN(cmpValue.value) ? (
              <span>{footerTransformer(round(cmpValue.value))}</span>
            ) : (
              <Tooltip title={translate('reports.template_insight.no_data')}>
                --
              </Tooltip>
            )}
            <FooterText>{translate('report.widget.preview-period')}</FooterText>
          </FooterWrapper>
        )}
      </Wrapper>
    );

    return (
      <BaseWidget>
        <BaseWidget.Title
          text={<TitleWithTooltip title={title} tooltip={tooltip} />}
        />
        <BaseWidget.Content>
          {WidgetState.map(resultRenderer, loadingRender, () =>
            noDataRender(noDataMessageFormatter(noDataMsg, translate)),
          )(value)}
        </BaseWidget.Content>
      </BaseWidget>
    );
  };
